import React, { useState, useEffect, createRef, useMemo, useCallback, memo } from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import * as reduxActions from "store/actions";
import reduxStore from "store/";
import history from "store/history";

// COMPONENTS
import HeaderWithMenu from "components/Header/card";
import ErrorBoundary from "components/ErrorBoundary";
import CustomButton from "components/Buttons/custom";
import ListLinks from "components/BrightAndConnected/listLinks";
import Radar from "views/BrightAndConnected/Radar";
import Insights from "views/BrightAndConnected/InsightBoards";

//DEPENDENCIES
import ResumeTasks from "./resumeTasks"
import MyChannels from "./myChannels"; // ADICIONADO: Import do MyChannels

// FUNCTIONS
import { translate, customApp } from "functions/";

// Memoizar o componente Card para evitar re-renders desnecessários
const Card = memo(({ title, icon, content, expandedBlocks, setExpandedBlocks, keyId }) => {
    const contentRef = createRef();
    const maxHeight = 177;
    const isExpanded = expandedBlocks[keyId];
    const [needsExpandButton, setNeedsExpandButton] = useState(false);

    // Memoizar função de toggle para evitar re-criação
    const toggleExpand = useCallback(() => {
        setExpandedBlocks((prev) => ({
            ...prev,
            [keyId]: !prev[keyId],
        }));
    }, [keyId, setExpandedBlocks]);

    useEffect(() => {
        const checkHeight = () => {
            if (contentRef.current) {
                const height = contentRef.current.scrollHeight;
                setNeedsExpandButton(height > maxHeight);
            }
        };

        // Usar requestAnimationFrame para melhor performance
        const rafId = requestAnimationFrame(() => {
            const timeout = setTimeout(checkHeight, 50);
            return () => clearTimeout(timeout);
        });

        return () => cancelAnimationFrame(rafId);
    }, [content, maxHeight]);

    // Memoizar estilos para evitar recálculos
    const cardStyles = useMemo(() => ({
        container: {
            position: "relative",
            backgroundColor: "#fff",
            boxShadow: "0px 2px 8px rgba(0,0,0,0.12)",
            borderRadius: "12px",
            padding: "0px 0px 30px 0px",
            display: "flex",
            flexDirection: "column",
            transition: "box-shadow 0.2s ease, transform 0.2s ease",
            cursor: "default",
        },
        content: {
            overflow: isExpanded ? "visible" : "hidden",
            transition: "height 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
            padding: "15px 15px 0px 15px",
            height: isExpanded ? "auto" : `${maxHeight}px`,
            position: "relative",
        },
        expandButton: {
            position: "absolute",
            paddingBottom: 15,
            paddingLeft: 15,
            paddingTop: 15,
            left: 0,
            right: 0,
            bottom: 0,
            marginTop: 8,
            alignSelf: "flex-start",
            background: "linear-gradient(180deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.9) 30%, rgba(255,255,255,1) 100%)",
            border: "none",
            color: customApp("primaryColor") || "#007BFF",
            cursor: "pointer",
            fontSize: "14px",
            fontWeight: "500",
            borderRadius: "0 0 12px 12px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            transition: "background 0.2s ease",
        }
    }), [isExpanded, maxHeight]);

    return (
        <div
            style={cardStyles.container}
            onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-2px)";
                e.currentTarget.style.boxShadow = "0px 4px 16px rgba(0,0,0,0.15)";
            }}
            onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0px)";
                e.currentTarget.style.boxShadow = "0px 2px 8px rgba(0,0,0,0.12)";
            }}
        >
            <HeaderWithMenu
                title={translate(title, 1)}
                icon={icon}
                color={customApp("colorText")}
                style={{
                    background: `linear-gradient(135deg, ${customApp("ColumnTitleColor")}, ${customApp("ColumnTitleColor")}dd)`,
                    padding: "18px 15px",
                    borderRadius: "12px 12px 0px 0px",
                    height: 40,
                    boxShadow: "0px 2px 4px rgba(0,0,0,0.1)"
                }}
            />
            <div ref={contentRef} style={cardStyles.content}>
                <div
                    dangerouslySetInnerHTML={{ __html: content }}
                    style={{
                        lineHeight: "1.6",
                        fontSize: "14px",
                        color: "#444"
                    }}
                />
            </div>
            {needsExpandButton && (
                <button
                    onClick={toggleExpand}
                    style={cardStyles.expandButton}
                    onMouseEnter={(e) => {
                        e.target.style.background = "linear-gradient(180deg, rgba(255,255,255,0) 0%, rgba(240,248,255,0.9) 30%, rgba(240,248,255,1) 100%)";
                    }}
                    onMouseLeave={(e) => {
                        e.target.style.background = "linear-gradient(180deg, rgba(255,255,255,0) 0%, rgba(255,255,255,0.9) 30%, rgba(255,255,255,1) 100%)";
                    }}
                >
                    {isExpanded ?
                        `↑ ${translate("$__readLess", 1)}` :
                        `↓ ${translate("$__readMore", 1)}`
                    }
                </button>
            )}
        </div>
    );
});

// Memoizar seções do dashboard
const DashboardSection = memo(({ title, icon, component, redirect }) => {
    const sectionStyles = useMemo(() => ({
        container: {
            position: "relative",
            backgroundColor: "#fff",
            boxShadow: "0px 2px 8px rgba(0,0,0,0.08)",
            borderRadius: "12px",
            marginBottom: "24px",
            overflow: "hidden",
            transition: "box-shadow 0.2s ease",
        },
        header: {
            position: "relative",
            height: "50px",
            marginBottom: "15px"
        },
        headerContent: {
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            height: "50px",
            background: `linear-gradient(135deg, ${customApp("ColumnTitleColor")}, ${customApp("ColumnTitleColor")}dd)`,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            color: customApp("colorText"),
            padding: "0 20px",
            boxShadow: "0px 2px 4px rgba(0,0,0,0.1)"
        }
    }), []);

    return (
        <div
            style={sectionStyles.container}
            onMouseEnter={(e) => {
                e.currentTarget.style.boxShadow = "0px 4px 16px rgba(0,0,0,0.12)";
            }}
            onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow = "0px 2px 8px rgba(0,0,0,0.08)";
            }}
        >
            <div style={sectionStyles.header}>
                <div style={sectionStyles.headerContent}>
                    <div style={{
                        fontSize: "16px",
                        fontWeight: "600",
                        display: "flex",
                        alignItems: "center",
                        gap: "8px"
                    }}>
                        <span className="material-icons" style={{ fontSize: "20px" }}>
                            {icon}
                        </span>
                        {translate(title, "*")}
                    </div>
                    <CustomButton
                        title={translate(`$__save`, "*")}
                        text={translate(`$__viewAll${title.split("$__")[1]}`)}
                        color={"white"}
                        icon={icon}
                        transparent
                        onClick={() => history.push(redirect)}
                        size={"25px"}
                    />
                </div>
            </div>
            <div style={{ padding: "0 20px 20px 20px" }}>
                <ErrorBoundary>{component}</ErrorBoundary>
            </div>
        </div>
    );
});

function DashboardResume() {
    const { session } = reduxStore.getState();
    const [expandedBlocks, setExpandedBlocks] = useState({});

    // Memoizar configuração das seções para melhor performance
    const dashboardSections = useMemo(() => [
        {
            title: "$__lastNews",
            icon: "feed",
            component: <ListLinks max={6} horizontal />,
            redirect: "bright-and-connected#tab/allNews",
        },
        {
            title: "$__lastAddRadar",
            icon: "settings_input_antenna",
            component: <Radar max={6} horizontal noPagination />,
            redirect: "bright-and-connected#tab/radar",
        },
        {
            title: "$__lastAddInsights",
            icon: "lightbulb",
            component: <Insights max={6} horizontal noPagination />,
            redirect: "bright-and-connected#tab/insight-boards",
        }
    ], []);

    return (
        <div style={{ width: "100%", padding: "16px" }}>
            <div
                style={{
                    display: "grid",
                    gridTemplateColumns: "repeat(2, 1fr)", // Duas colunas
                    gap: "16px",
                    alignItems: "start",
                }}
            >
                {session.GlobalData.strategicStatement && (
                    <Card
                        title="$__strategicStatement"
                        icon="history_edu"
                        content={session.GlobalData.strategicStatement}
                        expandedBlocks={expandedBlocks}
                        setExpandedBlocks={setExpandedBlocks}
                        keyId="strategicStatement"
                    />
                )}
                {session.GlobalData.message && (
                    <Card
                        title="$__presidentMessage"
                        icon="account_box"
                        content={session.GlobalData.message}
                        expandedBlocks={expandedBlocks}
                        setExpandedBlocks={setExpandedBlocks}
                        keyId="message"
                    />
                )}
                {session.GlobalData.vocation && (
                    <Card
                        title="$__vocation"
                        icon="spoke"
                        content={session.GlobalData.vocation}
                        expandedBlocks={expandedBlocks}
                        setExpandedBlocks={setExpandedBlocks}
                        keyId="vocation"
                    />
                )}
                {session.GlobalData.history && (
                    <Card
                        title="$__history"
                        icon="menu_book"
                        content={session.GlobalData.history}
                        expandedBlocks={expandedBlocks}
                        setExpandedBlocks={setExpandedBlocks}
                        keyId="history"
                    />
                )}
                {session.GlobalData.values && (
                    <Card
                        title="$__values"
                        icon="join_inner"
                        content={session.GlobalData.values}
                        expandedBlocks={expandedBlocks}
                        setExpandedBlocks={setExpandedBlocks}
                        keyId="values"
                    />
                )}
                {session.GlobalData.legacy && (
                    <Card
                        title="$__legacy"
                        icon="subtitles"
                        content={session.GlobalData.legacy}
                        expandedBlocks={expandedBlocks}
                        setExpandedBlocks={setExpandedBlocks}
                        keyId="legacy"
                    />
                )}
            </div>

            {/* Resume Tasks */}
            <ErrorBoundary>
                <ResumeTasks />
            </ErrorBoundary>

            <ErrorBoundary>
                <MyChannels /> {/* ADICIONADO: Componente MyChannels */}
            </ErrorBoundary>
            {/* Other sections */}
            <div style={{ marginTop: "16px", paddingBottom: "33px" }}>
                {dashboardSections.map(({ title, icon, component, redirect }) => (
                    <DashboardSection
                        key={title}
                        title={title}
                        icon={icon}
                        component={component}
                        redirect={redirect}
                    />
                ))}
            </div>
        </div>
    );
}

const mapDispatchToProps = (dispatch) => bindActionCreators(reduxActions, dispatch);

export default connect(null, mapDispatchToProps)(DashboardResume);